"use client";

import React from "react";
import IconChecking from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconChecking.js";
import IconSwimmingpool from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-linear/IconSwimmingpool.js";
import IconWifi from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconWifi.js";
import IconGym from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconGym.js";
import IconSpa from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconSpa.js";
import IconDrink from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconDrink.js";
import IconRestaurant from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconRestaurant.js";
import IconPet from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconPet.js";
import IconChild from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconChild.js";
import IconAdultsOnly from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconAdultsOnly.js";
import IconAirconditioned from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconAirconditioned.js";
import IconSandyBeach from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconSandyBeach.js";
import IconActivities from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconActivities.js";
import IconFamily from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconFamily.js";
import IconHealthSafety from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconHealthSafety.js";
import IconCasino from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconCasino.js";
import IconAccessibility from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconAccessibility.js";
import { Text } from "@lmn/design-system";
import { Headline } from "@/lib/Types/Headline";
import styled from "styled-components";
import media from "@/styles/mediaQueries";

export const FacilitiesContainer = styled.div(() => ({
  display: "flex",
  gap: "8px",
  overflowX: "auto",
  scrollSnapType: "x mandatory",
  WebkitOverflowScrolling: "touch",
  paddingBottom: "8px",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },

  ...media.screenMdMin({
    flexDirection: "column",
    overflowX: "visible",
    scrollSnapType: "none",
    paddingBottom: 0,
  }),
}));

export const FacilitiesRow = styled.div(() => ({
  display: "contents",

  ...media.screenMdMin({
    display: "flex",
    gap: "8px",
  }),
}));

export const FacilityCard = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px",
  border: `1px solid ${theme.kallisto.color.border.static.borderDisabled}`,
  borderRadius: theme.kallisto.border.radius.radius100,
  gap: "8px",
  minHeight: "80px",
  textAlign: "center",
  // Mobile: fixed width for horizontal scroll
  minWidth: "80px",
  flexShrink: 0,
  scrollSnapAlign: "start",
  svg: {
    color: theme.kallisto.color.icon.static.iconSecondary,
  },
  ...media.screenMdMin({
    flex: 1,
    minWidth: "auto",
    padding: "8px",
  }),
}));

// Priority facility IDs (show first if present) - based on most common/important amenities
const priorityFacilityIds = [
  46, // WLAN
  64, // Behindertengerecht
  33, // Pool
  41, // Spa
  7, // Fitness
  28, // Clubs (Bar)
  68, // Restaurant
  61, // Klimaanlage
  20, // Kinderbetreuung
  79, // Haustiere erlaubt
];

// Icon mapping by facility name
const facilityIconMap: Record<
  string,
  React.ComponentType<{ size?: number }>
> = {
  WLAN: IconWifi,
  Internetzugang: IconWifi,
  Pool: IconSwimmingpool,
  Hallenbad: IconSwimmingpool,
  Kinderpool: IconSwimmingpool,
  Spa: IconSpa,
  Massage: IconSpa,
  Sauna: IconSpa,
  Whirlpool: IconSpa,
  Hammam: IconSpa,
  Fitness: IconGym,
  Behindertengerecht: IconAccessibility,
  Klimaanlage: IconAirconditioned,
  Clubs: IconDrink,
  "Haustiere erlaubt": IconPet,
  Restaurant: IconRestaurant,
  Kinderbetreuung: IconChild,
  Animationsprogramm: IconChild,
  Spielplatz: IconChild,
  Kindermenues: IconChild,
  Erwachsenenhotel: IconAdultsOnly,
  Sandstrand: IconSandyBeach,
  Strandnah: IconSandyBeach,
  "Direkte Strandlage": IconSandyBeach,
  Golf: IconActivities,
  Familienfreundlich: IconFamily,
  Casino: IconCasino,
  Abstandsregeln: IconHealthSafety,
  Desinfektionsmittelspender: IconHealthSafety,
  "Hand-Desinfektionsmittel": IconHealthSafety,
};

const DefaultIcon = IconChecking;

interface FacilityType {
  id: number;
  name: string;
}

interface MainFacilitiesProps {
  headlines: Headline[];
  maxDisplay?: number;
}

const processFacilities = (
  headlines: Headline[],
  maxDisplay: number
): FacilityType[] => {
  if (!headlines?.length) return [];

  // Flatten all category types from all headlines
  const allFacilities = headlines.flatMap(
    (headline) => headline.categories?.map((cat) => cat.type) || []
  );

  // Remove duplicates by ID
  const uniqueFacilities = allFacilities.reduce<FacilityType[]>(
    (acc, facility) => {
      if (!acc.find((f) => f.id === facility.id)) {
        acc.push(facility);
      }
      return acc;
    },
    []
  );

  // Separate priority facilities from remaining
  const priorityFacilities: FacilityType[] = [];
  const remainingFacilities: FacilityType[] = [];

  uniqueFacilities.forEach((facility) => {
    if (priorityFacilityIds.includes(facility.id)) {
      priorityFacilities.push(facility);
    } else {
      remainingFacilities.push(facility);
    }
  });

  // Sort priority facilities by their predefined order
  priorityFacilities.sort((a, b) => {
    const indexA = priorityFacilityIds.indexOf(a.id);
    const indexB = priorityFacilityIds.indexOf(b.id);
    return indexA - indexB;
  });

  // Combine priority + remaining, limited to maxDisplay
  return [...priorityFacilities, ...remainingFacilities].slice(0, maxDisplay);
};

const splitIntoRows = (
  items: FacilityType[]
): [FacilityType[], FacilityType[]] => {
  if (items.length <= 5) {
    // Single row
    return [items, []];
  }
  // Two rows: split with first row having more items if odd count
  const firstRowCount = Math.ceil(items.length / 2);
  return [items.slice(0, firstRowCount), items.slice(firstRowCount)];
};

const MainFacilities: React.FC<MainFacilitiesProps> = ({
  headlines,
  maxDisplay = 10,
}) => {
  const facilities = processFacilities(headlines, maxDisplay);

  if (!facilities.length) {
    return null;
  }

  const [firstRow, secondRow] = splitIntoRows(facilities);

  const getIconForFacility = (facilityName: string) => {
    const IconComponent = facilityIconMap[facilityName] || DefaultIcon;
    return <IconComponent size={24} />;
  };

  return (
    <FacilitiesContainer>
      <FacilitiesRow>
        {firstRow.map((facility) => (
          <FacilityCard key={facility.id}>
            {getIconForFacility(facility.name)}
            <Text variant="BodySmall">{facility.name}</Text>
          </FacilityCard>
        ))}
      </FacilitiesRow>
      {secondRow.length > 0 && (
        <FacilitiesRow>
          {secondRow.map((facility) => (
            <FacilityCard key={facility.id}>
              {getIconForFacility(facility.name)}
              <Text variant="BodySmall">{facility.name}</Text>
            </FacilityCard>
          ))}
        </FacilitiesRow>
      )}
    </FacilitiesContainer>
  );
};

export default MainFacilities;
